// Supabase Realtime channel multiplexer.
// Creates ONE channel per unique table across all subscribers.
// On any change → debounced refetch of all callbacks registered for that
// table. Shared by DataTable v2 (via `realtimeTables`), the dashboard
// widgets, and any task-queue peek that opts in.
//
// Was previously "DashboardRealtimeContext" — renamed because the
// functionality isn't dashboard-specific.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabaseClient } from "../utility/supabaseClient";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { RealtimeChannelsProvider } from "@ds/actions/RealtimeChannelsContext";
import { useRealtimeChannels } from "@ds/actions/useRealtimeChannels";

const DEBOUNCE_MS = 500;
const JITTER_MAX_MS = 2000; // Random delay per client so 15 browsers don't all refetch simultaneously

// `useRealtimeChannels` is NOT re-exported here — import it directly from
// `@ds/actions/useRealtimeChannels`. The old re-export existed to keep this
// module's consumers (and the locked test) on a `./RealtimeContext` import
// path, but a re-export makes this a barrel and trips
// react-refresh/only-export-components. All three consumers now import the
// hook from its source module.

// ── Provider ────────────────────────────────────────────────────────────

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Pass-through if an ancestor provider is already mounted. Nested
  // mounts inherit the ancestor's registry + channel cache, so "N tabs
  // mounting the same page = 1 channel per table," not N channels.
  const ancestor = useRealtimeChannels();

  // table → Set of refetch callbacks
  const registryRef = useRef<Map<string, Set<() => void>>>(new Map());
  // table → active Supabase channel
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());
  // table → debounce timer
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Per-client jitter — computed once on mount, stable for the session.
  // Lazy useState initializer keeps the impure Math.random() out of the
  // render body while preserving a single stable value per provider instance.
  const [jitter] = useState(() => Math.random() * JITTER_MAX_MS);

  const debouncedRefetchAll = useCallback((table: string) => {
    // Clear existing timer for this table
    const existing = timersRef.current.get(table);
    if (existing) clearTimeout(existing);

    // Debounce (batches rapid events) + jitter (spreads clients over 0-2s)
    const delay = DEBOUNCE_MS + jitter;
    timersRef.current.set(table, setTimeout(() => {
      const callbacks = registryRef.current.get(table);
      if (callbacks) {
        callbacks.forEach((refetch) => refetch());
      }
      timersRef.current.delete(table);
    }, delay));
  }, [jitter]);

  const ensureChannel = useCallback((table: string) => {
    if (channelsRef.current.has(table)) return;

    const channel = supabaseClient
      .channel(`realtime:${table}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        () => debouncedRefetchAll(table),
      )
      .subscribe();

    channelsRef.current.set(table, channel);
  }, [debouncedRefetchAll]);

  const removeChannelIfEmpty = useCallback((table: string) => {
    const callbacks = registryRef.current.get(table);
    if (callbacks && callbacks.size > 0) return;

    const channel = channelsRef.current.get(table);
    if (channel) {
      supabaseClient.removeChannel(channel);
      channelsRef.current.delete(table);
    }
    registryRef.current.delete(table);
  }, []);

  const register = useCallback((table: string, refetch: () => void) => {
    let callbacks = registryRef.current.get(table);
    if (!callbacks) {
      callbacks = new Set();
      registryRef.current.set(table, callbacks);
    }
    callbacks.add(refetch);
    ensureChannel(table);
  }, [ensureChannel]);

  const unregister = useCallback((table: string, refetch: () => void) => {
    const callbacks = registryRef.current.get(table);
    if (callbacks) {
      callbacks.delete(refetch);
      removeChannelIfEmpty(table);
    }
  }, [removeChannelIfEmpty]);

  // Cleanup all channels on unmount
  useEffect(() => {
    // `.current` is never reassigned on these refs (the Maps are created once
    // and only mutated), so capturing them here is identical to reading
    // `.current` in the cleanup — it just satisfies the ref-in-cleanup lint.
    const channels = channelsRef.current;
    const timers = timersRef.current;
    return () => {
      channels.forEach((channel) => {
        supabaseClient.removeChannel(channel);
      });
      channels.clear();
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  if (ancestor) {
    // Ancestor owns realtime — don't create a second map. The
    // ancestor's Context.Provider is already in scope, so
    // `useRealtimeChannels` consumers below us see its value.
    return <>{children}</>;
  }

  return (
    <RealtimeChannelsProvider value={{ register, unregister }}>
      {children}
    </RealtimeChannelsProvider>
  );
};
